import { useMutation, useQuery } from "@apollo/react-hooks";
import styled from "@emotion/styled";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { gql } from "apollo-boost";
import React, { FC, useState } from "react";
import BlockContainer from "../../components/BlockContainer";
import CSAButton from "../../components/CSAButton";
import CSAInput from "../../components/CSAInput";
import DataCell from "../../components/DataCell";
import HeaderCell from "../../components/HeaderCell";
import Modal from "../../components/Modal";
import Notification, { NotificationProps } from "../../components/Notification";
import formatDate from "../../helpers/format-date";
import parseDate from "../../helpers/parse-date";
import { CurrentUser } from "../../queries";
import { Response } from "../../types";
import EditImplementationStorageForm from "./EditImplementationStorageForm";

const Section = styled.section`
  position: relative;
`;

const Controls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const SearchInputContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const SearchInput = styled(CSAInput)`
  margin-right: 1rem;
`;

const ClearSearchButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  color: var(--csa-darkgrey);
  display: inline-block;
  outline: none;
  position: absolute;
  right: 1.5rem;
  top: 1rem;
  z-index: 1;

  &:hover {
    cursor: pointer;
  }
`;

const TableContainer = styled(BlockContainer)`
  margin-top: 3rem;
  max-width: 70rem;
  position: relative;
`;

const Table = styled.table`
  font-size: 1rem;
  min-width: 60rem;
  width: 100%;

  & th,
  td {
    padding: 0 0.5rem;
  }

  & td:last-child {
    padding-right: 0;
  }
`;

const TableHeaderCell = styled(HeaderCell)`
  font-weight: bold;
  white-space: nowrap;
`;

const ControlsCell = styled.td`
  text-align: right;
  width: 5rem;
`;

const ActionButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  color: var(--csa-blue);
  display: inline-block;
  outline: none;
  margin-right: 0.5rem;

  &:hover {
    cursor: pointer;
  }
`;

const ResourceLink = styled.a`
  color: var(--csa-blue);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteModal = styled.div`
  max-width: 22rem;
  text-align: center;
`;

const ConfirmationMessage = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
`;

const IMPLEMENTATION_STORAGES = gql`
  query GetImplementationStorages {
    implementationStorages {
      id
      name
      resourceLink
      dateOfCompletion
    }
  }
`;

export interface ImplementationStorageData {
  id?: string;
  name?: string;
  resourceLink?: string;
  dateOfCompletion?: string;
}

export interface ImplementationStoragesData {
  implementationStorages: ImplementationStorageData[];
}

const ADD_IMPLEMENTATION_STORAGE = gql`
  mutation AddImplementationStorage(
    $name: String!
    $resourceLink: String!
    $dateOfCompletion: String!
  ) {
    addImplementationStorage(
      name: $name
      resourceLink: $resourceLink
      dateOfCompletion: $dateOfCompletion
    ) {
      code
      message
    }
  }
`;

export interface AddImplementationStorageVariables {
  name: string;
  resourceLink: string;
  dateOfCompletion: string;
}

const DELETE_IMPLEMENTATION_STORAGE = gql`
  mutation DeleteImplementationStorage($id: ID!) {
    deleteImplementationStorage(id: $id) {
      code
      message
    }
  }
`;

export interface DeleteImplementationStorageVariables {
  id: string;
}

const EDIT_IMPLEMENTATION_STORAGE = gql`
  mutation EditImplementationStorage(
    $id: ID!
    $name: String!
    $resourceLink: String!
    $dateOfCompletion: String!
  ) {
    editImplementationStorage(
      id: $id
      name: $name
      resourceLink: $resourceLink
      dateOfCompletion: $dateOfCompletion
    ) {
      code
      message
    }
  }
`;

export interface EditImplementationStorageVariables {
  id: string;
  name: string;
  resourceLink: string;
  dateOfCompletion: string;
}

export interface ImplementationStorageProps {
  currentUser?: CurrentUser;
}

const ImplementationStorage: FC<ImplementationStorageProps> = ({
  currentUser,
}) => {
  const [notification, setNotification] = useState<NotificationProps>({});
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingImplementationStorage, setEditingImplementationStorage] =
    useState<ImplementationStorageData | undefined>();
  const [deletingImplementationStorage, setDeletingImplementationStorage] =
    useState<string | undefined>();
  const [search, setSearch] = useState("");

  const { data } = useQuery<ImplementationStoragesData, void>(
    IMPLEMENTATION_STORAGES,
    {
      fetchPolicy: "cache-and-network",
    },
  );

  const [addImplementationStorage] = useMutation<
    Response,
    AddImplementationStorageVariables
  >(ADD_IMPLEMENTATION_STORAGE, {
    onCompleted: () =>
      setNotification({
        children: <p>Added successfully</p>,
        type: "success",
      }),
    onError: () =>
      setNotification({
        children: <p>Sorry something went wrong</p>,
        type: "error",
      }),
    refetchQueries: [{ query: IMPLEMENTATION_STORAGES }],
  });

  const [deleteImplementationStorage] = useMutation<
    Response,
    DeleteImplementationStorageVariables
  >(DELETE_IMPLEMENTATION_STORAGE, {
    onCompleted: () =>
      setNotification({
        children: <p>Deleted successfully</p>,
        type: "success",
      }),
    onError: () =>
      setNotification({
        children: <p>Sorry something went wrong</p>,
        type: "error",
      }),
    refetchQueries: [{ query: IMPLEMENTATION_STORAGES }],
  });

  const [editImplementationStorage] = useMutation<
    Response,
    EditImplementationStorageVariables
  >(EDIT_IMPLEMENTATION_STORAGE, {
    onCompleted: () =>
      setNotification({
        children: <p>Edited successfully</p>,
        type: "success",
      }),
    onError: () =>
      setNotification({
        children: <p>Sorry something went wrong</p>,
        type: "error",
      }),
    refetchQueries: [{ query: IMPLEMENTATION_STORAGES }],
  });

  const filteredData = data?.implementationStorages.filter((item) => {
    if (!search) {
      return true;
    }

    let isInFilter = false;
    Object.keys(item).forEach((key) => {
      if (
        `${item[key as keyof ImplementationStorageData]}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ) {
        isInFilter = true;
      }
    });

    return isInFilter;
  });

  return (
    <>
      <Notification fadeOut float type={notification.type} width="100%">
        {notification.children}
      </Notification>

      <Section>
        <h1>Implementation Storage</h1>

        <Controls>
          <SearchInputContainer>
            <SearchInput
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              type="text"
              value={search}
            />
            {search && (
              <ClearSearchButton onClick={() => setSearch("")} type="button">
                <FontAwesomeIcon icon={faTimes} />
              </ClearSearchButton>
            )}
          </SearchInputContainer>
          <CSAButton
            disabled={!currentUser?.active}
            onClick={() => setIsAdding(true)}
            type="button"
          >
            Add Implementation
          </CSAButton>
        </Controls>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Resource Link</TableHeaderCell>
                <TableHeaderCell>Date of Completion</TableHeaderCell>
                <th aria-label="Edit" />
              </tr>
            </thead>
            <tbody>
              {filteredData?.map((item) => (
                <tr key={item.id}>
                  <DataCell>{item.name}</DataCell>
                  <DataCell>
                    {item.resourceLink && (
                      <ResourceLink
                        href={item.resourceLink}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {item.resourceLink}
                      </ResourceLink>
                    )}
                  </DataCell>
                  <DataCell>
                    {item.dateOfCompletion
                      ? formatDate(parseDate(item.dateOfCompletion), {
                          showDate: true,
                          showMonth: true,
                          showYear: true,
                        })
                      : ""}
                  </DataCell>
                  <ControlsCell>
                    <ActionButton
                      disabled={!currentUser?.active}
                      onClick={() => {
                        setEditingImplementationStorage(item);
                        setIsEditing(true);
                      }}
                      type="button"
                    >
                      Edit
                    </ActionButton>
                    <ActionButton
                      disabled={!currentUser?.active}
                      onClick={() => {
                        setDeletingImplementationStorage(item.id);
                        setIsDeleting(true);
                      }}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </ActionButton>
                  </ControlsCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </Section>

      <Modal onClose={() => setIsAdding(false)} show={isAdding}>
        <EditImplementationStorageForm
          onSubmit={async (variables) => {
            await addImplementationStorage({ variables });
            setIsAdding(false);
          }}
        />
      </Modal>

      <Modal
        onClose={() => {
          setIsEditing(false);
          setEditingImplementationStorage(undefined);
        }}
        show={isEditing}
      >
        <EditImplementationStorageForm
          implementationStorage={editingImplementationStorage}
          onSubmit={async (variables) => {
            if (editingImplementationStorage?.id) {
              await editImplementationStorage({
                variables: {
                  ...variables,
                  id: editingImplementationStorage.id,
                },
              });
            }
            setIsEditing(false);
            setEditingImplementationStorage(undefined);
          }}
        />
      </Modal>

      <Modal
        onClose={() => {
          setIsDeleting(false);
          setDeletingImplementationStorage(undefined);
        }}
        show={isDeleting}
      >
        <DeleteModal>
          <ConfirmationMessage>
            Are you sure you want to delete this implementation?
          </ConfirmationMessage>
          <CSAButton
            onClick={async () => {
              setIsDeleting(false);
              setDeletingImplementationStorage(undefined);

              if (deletingImplementationStorage) {
                await deleteImplementationStorage({
                  variables: {
                    id: deletingImplementationStorage,
                  },
                });
              }
            }}
            style={{ width: "100%" }}
            type="button"
          >
            Delete
          </CSAButton>
        </DeleteModal>
      </Modal>
    </>
  );
};

export default ImplementationStorage;
